import { Container, Input, Key, SelectList, Text, type SelectItem, getEditorKeybindings, matchesKey } from '@mariozechner/pi-tui';
import { PROVIDERS, type Model } from '../utils/model.js';
import type { ApprovalDecision } from '../agent/types.js';
import type { SkillMetadata } from '../skills/types.js';
import { selectListTheme, theme } from '../theme.js';

export class VimSelectList extends SelectList {
  onCtrlC?: () => void;

  handleInput(keyData: string): void {
    if (matchesKey(keyData, Key.ctrl('c'))) {
      this.onCtrlC?.();
      return;
    }
    if (keyData === 'j') {
      super.handleInput('\u001b[B');
      return;
    }
    if (keyData === 'k') {
      super.handleInput('\u001b[A');
      return;
    }
    super.handleInput(keyData);
  }
}

class EmptyModelSelector extends Container {
  private readonly onCancel: () => void;
  onCtrlC?: () => void;

  constructor(providerId: string, onCancel: () => void) {
    super();
    this.onCancel = onCancel;
    this.addChild(new Text(theme.muted('No models available.'), 0, 0));
    if (providerId === 'ollama') {
      this.addChild(
        new Text(theme.muted('Make sure Ollama is running and you have models downloaded.'), 0, 0),
      );
    }
    this.addChild(new Text(theme.muted('esc to go back'), 0, 0));
  }

  handleInput(keyData: string): void {
    const kb = getEditorKeybindings();
    if (matchesKey(keyData, Key.ctrl('c'))) {
      this.onCtrlC?.();
      return;
    }
    if (kb.matches(keyData, 'selectCancel')) {
      this.onCancel();
    }
  }
}

export function createProviderSelector(
  currentProvider: string | undefined,
  onSelect: (providerId: string | null) => void,
) {
  const items: SelectItem[] = PROVIDERS.map((provider, index) => ({
    value: provider.providerId,
    label: `${index + 1}. ${provider.displayName}${currentProvider === provider.providerId ? ' ✓' : ''}`,
  }));
  const list = new VimSelectList(items, 8, selectListTheme);
  list.onSelect = (item) => onSelect(item.value);
  list.onCancel = () => onSelect(null);
  return list;
}

export function createSearchProviderSelector(
  currentProvider: string,
  onSelect: (providerId: 'exa' | 'perplexity' | 'tavily' | 'langsearch') => void,
  onCancel: () => void,
) {
  const providers: { id: 'exa' | 'perplexity' | 'tavily' | 'langsearch'; displayName: string }[] = [
    { id: 'exa', displayName: 'Exa' },
    { id: 'perplexity', displayName: 'Perplexity' },
    { id: 'tavily', displayName: 'Tavily' },
    { id: 'langsearch', displayName: 'LangSearch' },
  ];
  const items: SelectItem[] = providers.map((provider, index) => ({
    value: provider.id,
    label: `${index + 1}. ${provider.displayName}${currentProvider === provider.id ? ' ✓' : ''}`,
  }));
  const list = new VimSelectList(items, 5, selectListTheme);
  list.onSelect = (item) => onSelect(item.value as 'exa' | 'perplexity' | 'tavily' | 'langsearch');
  list.onCancel = () => onCancel();
  return list;
}

export function createModelSelector(
  models: Model[],
  currentModel: string | undefined,
  onSelect: (modelId: string | null) => void,
  providerId?: string,
) {
  if (models.length === 0) {
    return new EmptyModelSelector(providerId ?? '', () => onSelect(null));
  }
  const items: SelectItem[] = models.map((model, index) => ({
    value: model.id,
    label: `${index + 1}. ${model.displayName}${currentModel === model.id ? ' ✓' : ''}`,
  }));
  const list = new VimSelectList(items, 10, selectListTheme);
  list.onSelect = (item) => onSelect(item.value);
  list.onCancel = () => onSelect(null);
  return list;
}

export function createApprovalSelector(onSelect: (decision: ApprovalDecision) => void) {
  const items: SelectItem[] = [
    { value: 'allow-once', label: '1. Yes' },
    { value: 'allow-session', label: '2. Yes, allow all edits this session' },
    { value: 'deny', label: '3. No' },
  ];
  const list = new VimSelectList(items, 5, selectListTheme);
  list.onSelect = (item) => onSelect(item.value as ApprovalDecision);
  list.onCancel = () => onSelect('deny');
  return list;
}

export function createKeyServiceSelector(
  items: { value: string; label: string }[],
  onSelect: (serviceId: string | null) => void,
) {
  const selectItems: SelectItem[] = items.map((item, index) => ({
    value: item.value,
    label: `${index + 1}. ${item.label}`,
  }));
  const list = new VimSelectList(selectItems, 12, selectListTheme);
  list.onSelect = (item) => onSelect(item.value);
  list.onCancel = () => onSelect(null);
  return list;
}

export function createKeyActionSelector(
  hasKey: boolean,
  onSelect: (action: 'set' | 'remove' | null) => void,
) {
  const items: SelectItem[] = [
    { value: 'set', label: hasKey ? '1. Update key' : '1. Set key' },
  ];
  if (hasKey) {
    items.push({ value: 'remove', label: '2. Remove key' });
  }
  const list = new VimSelectList(items, 4, selectListTheme);
  list.onSelect = (item) => onSelect(item.value as 'set' | 'remove');
  list.onCancel = () => onSelect(null);
  return list;
}

export function createApiKeyConfirmSelector(onConfirm: (wantsToSet: boolean) => void) {
  const items: SelectItem[] = [
    { value: 'yes', label: '1. Yes' },
    { value: 'no', label: '2. No' },
  ];
  const list = new VimSelectList(items, 4, selectListTheme);
  list.onSelect = (item) => onConfirm(item.value === 'yes');
  list.onCancel = () => onConfirm(false);
  return list;
}

export function createDrishtiApiKeyActionSelector(
  onSelect: (action: 'have_key' | 'create_key' | null) => void,
) {
  const items: SelectItem[] = [
    { value: 'have_key', label: 'I already have an API key' },
    { value: 'create_key', label: 'Create a new API key' },
  ];
  const list = new VimSelectList(items, 4, selectListTheme);
  list.onSelect = (item) => onSelect(item.value as 'have_key' | 'create_key');
  list.onCancel = () => onSelect(null);
  return list;
}

export function createFactoryResetScopeSelector(
  onSelect: (scope: 'all' | 'setup' | 'credentials' | null) => void,
) {
  const items: SelectItem[] = [
    { value: 'all', label: '1. All Max data' },
    { value: 'setup', label: '2. Setup only' },
    { value: 'credentials', label: '3. Credentials only' },
    { value: 'cancel', label: '4. Cancel' },
  ];
  const list = new VimSelectList(items, 6, selectListTheme);
  list.onSelect = (item) => onSelect(item.value === 'cancel' ? null : item.value as 'all' | 'setup' | 'credentials');
  list.onCancel = () => onSelect(null);
  return list;
}

export function createSkillSelector(
  skills: SkillMetadata[],
  onSelect: (skillName: string | null) => void,
) {
  const items: SelectItem[] = skills.map((skill, index) => ({
    value: skill.name,
    label: `${index + 1}. ${skill.name} (${skill.source})`,
  }));
  items.push({ value: '__cancel__', label: `${items.length + 1}. Cancel` });
  const list = new VimSelectList(items, 14, selectListTheme);
  list.onSelect = (item) => onSelect(item.value === '__cancel__' ? null : item.value);
  list.onCancel = () => onSelect(null);
  return list;
}

export function createSkillActionSelector(
  skill: SkillMetadata,
  onSelect: (action: 'delete' | 'cancel' | null) => void,
) {
  const items: SelectItem[] = [];
  if (skill.source !== 'builtin') {
    items.push({ value: 'delete', label: '1. Delete skill' });
  }
  items.push({ value: 'cancel', label: `${items.length + 1}. Back` });
  const list = new VimSelectList(items, 4, selectListTheme);
  list.onSelect = (item) => onSelect(item.value as 'delete' | 'cancel');
  list.onCancel = () => onSelect(null);
  return list;
}

export function createDeleteSkillConfirmSelector(
  onSelect: (confirmed: boolean) => void,
) {
  const items: SelectItem[] = [
    { value: 'yes', label: '1. Yes, delete it' },
    { value: 'no', label: '2. No, keep it' },
  ];
  const list = new VimSelectList(items, 4, selectListTheme);
  list.onSelect = (item) => onSelect(item.value === 'yes');
  list.onCancel = () => onSelect(false);
  return list;
}

export class ApiKeyInputComponent {
  private readonly input = new Input();
  private readonly masked: boolean;
  onSubmit?: (apiKey: string | null) => void;
  onCancel?: () => void;
  onCtrlC?: () => void;

  constructor(masked = false) {
    this.masked = masked;
  }

  invalidate() {
    this.input.invalidate();
  }

  render(width: number): string[] {
    const lines = this.input.render(Math.max(10, width - 4));
    const raw = lines[0] ?? '';
    const display = this.masked
      ? `${'*'.repeat(this.input.getValue().length)}${this.input.getValue().length === 0 ? '█' : ''}`
      : raw;
    return [
      `${theme.primary('> ')}${display}`,
      theme.muted('Enter to confirm · Esc to cancel'),
    ];
  }

  handleInput(keyData: string): void {
    const kb = getEditorKeybindings();
    if (matchesKey(keyData, Key.ctrl('c'))) {
      this.onCtrlC?.();
      return;
    }
    if (kb.matches(keyData, 'submit')) {
      this.onSubmit?.(this.input.getValue().trim() || null);
      return;
    }
    if (kb.matches(keyData, 'selectCancel')) {
      this.onCancel?.();
      return;
    }
    this.input.handleInput(keyData);
  }

  getValue(): string {
    return this.input.getValue();
  }
}
